import React, { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import {
  Laptop, Smartphone, Speaker, Network,
  Printer, Wrench, ShoppingBag, Building2
} from 'lucide-react';

const services = [
  {
    icon: <Laptop size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'Laptop & Desktop Sales',
    desc: 'Wide range of laptops and desktops from top brands. New and refurbished options available to suit every budget.',
  },
  {
    icon: <Smartphone size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'Smartphone Sales',
    desc: 'Latest smartphones from Samsung, Apple, Tecno, Infinix and more. Genuine products with warranty.',
  },
  {
    icon: <Speaker size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'PA Systems',
    desc: 'Professional PA systems for events, churches, schools, and businesses. Installation and setup available.',
  },
  {
    icon: <Network size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'Networking Solutions',
    desc: 'Routers, switches, cabling, and full network setup for homes and businesses.',
  },
  {
    icon: <Printer size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'Printers & Consumables',
    desc: 'Laser and inkjet printers, toner cartridges, and ink refills. Home and office solutions.',
  },
  {
    icon: <Wrench size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'Repairs & Maintenance',
    desc: 'Professional computer and device repair services. Fast turnaround with quality parts.',
  },
  {
    icon: <ShoppingBag size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'Accessories & Bags',
    desc: 'Laptop bags, keyboards, mice, cables, adapters, and all the accessories you need.',
  },
  {
    icon: <Building2 size={26} color="#CC0000" strokeWidth={1.8} />,
    title: 'Corporate Supply',
    desc: 'Bulk supply for businesses, schools, and organizations with competitive pricing and invoicing.',
  },
];

const Services = () => {
  const { theme } = useContext(ThemeContext);

  return (
    <main style={styles.main(theme)}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={styles.pill}>What We Offer</span>
          <h1 style={styles.title}>Our Services</h1>
          <p style={styles.sub}>
            From sales to setup and repairs — Techdave Solutions is your complete technology partner in Harare.
          </p>
        </div>

        <div style={styles.grid}>
          {services.map((s) => (
            <div key={s.title} style={styles.card(theme)}>
              <div style={styles.iconBox}>{s.icon}</div>
              <h3 style={styles.cardTitle}>{s.title}</h3>
              <p style={styles.cardDesc}>{s.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={styles.cta(theme)}>
          <h2 style={styles.ctaTitle}>Need something specific?</h2>
          <p style={styles.ctaSub}>Contact us and we'll find the right solution for you.</p>
          <a href="/contact" style={styles.ctaBtn}>
            Get in Touch →
          </a>
        </div>
      </div>
    </main>
  );
};

const styles = {
  main: (theme) => ({
    minHeight: '100vh',
    paddingTop: '72px',
    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f5f5f5',
    color: theme === 'dark' ? '#ffffff' : '#111111',
  }),
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '60px 24px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '56px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  pill: {
    background: 'rgba(204,0,0,0.12)',
    border: '1px solid rgba(204,0,0,0.3)',
    color: '#CC0000',
    padding: '6px 16px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: '600',
  },
  title: {
    fontSize: 'clamp(32px, 5vw, 52px)',
    fontWeight: '900',
    letterSpacing: '-0.02em',
    color: '#111111',
  },
  sub: {
    fontSize: '17px',
    color: '#111111',
    maxWidth: '560px',
    lineHeight: 1.6,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '24px',
    marginBottom: '64px',
  },
  card: (theme) => ({
    backgroundColor: theme === 'dark' ? '#242424' : '#ffffff',
    borderRadius: '14px',
    padding: '28px',
    border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    transition: 'all 0.2s ease',
  }),
  iconBox: {
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    backgroundColor: 'rgba(204,0,0,0.08)',
    border: '1px solid rgba(204,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#CC0000',
  },
  cardDesc: {
    fontSize: '14px',
    lineHeight: 1.65,
    color: '#111111',
  },
  cta: (theme) => ({
    background: 'linear-gradient(135deg, #CC0000, #990000)',
    borderRadius: '20px',
    padding: '56px 40px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 8px 40px rgba(204,0,0,0.3)',
  }),
  ctaTitle: {
    fontSize: 'clamp(24px, 3vw, 36px)',
    fontWeight: '900',
    color: '#ffffff',
  },
  ctaSub: {
    fontSize: '17px',
    color: 'rgba(255,255,255,0.85)',
  },
  ctaBtn: {
    marginTop: '8px',
    padding: '14px 32px',
    backgroundColor: '#ffffff',
    color: '#CC0000',
    borderRadius: '10px',
    fontWeight: '800',
    fontSize: '15px',
    textDecoration: 'none',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
};

export default Services;